/**
 * useV3EditorActions — V3 编辑器操作 Hook
 *
 * 从旧版 useGanttInteraction 提取的纯 API 调用逻辑。
 * 负责：拖拽更新、自动排程、删除等 CRUD 操作。CRUD 完成后通过
 * onResourceRefresh 触发编辑器重拉 resource-editor 聚合（约束/共享组等），
 * 该聚合的唯一数据源在编辑器侧，避免本 hook 重复发起聚合查询。
 */

import { useState, useCallback } from 'react';
import { api } from '../../services/api';
import { wxbToast } from '../wxb-ui';
import { processTemplateV2Api } from '../../services';
import type { GanttNode, StageOperation } from '../ProcessTemplateGantt/types';
import { buildDraggedOperationTimingUpdate } from './dragTiming';
import { parseScheduleIdFromTaskId } from './taskIds';

const API = '';

// ---------------------------------------------------------------------------
// Options & Return types
// ---------------------------------------------------------------------------

export interface UseV3EditorActionsOptions {
  templateId: number;
  ganttNodes: GanttNode[];
  refreshData: () => Promise<void>;
  /**
   * Re-pull the resource-editor aggregate (constraints/share-groups/stages/…).
   * The editor owns that fetch as the single source of truth; this hook only
   * triggers it after CRUD operations so the constraint graph stays in sync.
   */
  onResourceRefresh?: () => Promise<unknown>;
}

export interface UseV3EditorActionsReturn {
  /** Loading state */
  loading: boolean;
  /** Handle drag end on a task bar */
  handleDragEnd: (taskId: string, newStart: number, newEnd: number) => Promise<boolean>;
  /** Handle resize end on a task bar */
  handleResizeEnd: (taskId: string, newStart: number, newEnd: number) => Promise<boolean>;
  /** Handle cascade group/equipment summary-bar drag end — shifts all affected operations by deltaHours */
  handleGroupDragEnd: (groupId: string, deltaHours: number, affectedTaskIds: string[]) => Promise<boolean>;
  /** Handle multi-select drag end — moves each task to its provided new start/end in one batch */
  handleTasksDragEnd: (updates: Array<{ taskId: string; newStart: number; newEnd: number }>) => Promise<boolean>;
  /** Delete a scheduled operation */
  handleDeleteTask: (taskId: string) => Promise<void>;
  /** Trigger auto-schedule for the template */
  handleAutoSchedule: () => Promise<void>;
  /** Re-pull the resource-editor aggregate (constraints / share groups / …) */
  refreshAll: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Find a GanttNode (operation) by schedule ID from the gantt node tree.
 */
const findOperationNode = (nodes: GanttNode[], scheduleId: number): GanttNode | null => {
  for (const node of nodes) {
    if (node.type === 'operation') {
      const opData = node.data as StageOperation | undefined;
      if (opData?.id === scheduleId) return node;
    }
    if (node.children) {
      const found = findOperationNode(node.children, scheduleId);
      if (found) return found;
    }
  }
  return null;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useV3EditorActions({
  templateId,
  ganttNodes,
  refreshData,
  onResourceRefresh,
}: UseV3EditorActionsOptions): UseV3EditorActionsReturn {
  const [loading, setLoading] = useState(false);

  // ---- Re-pull the resource-editor aggregate (single source of truth lives
  //      in the editor; this just forwards the request after CRUD). ----
  const refreshResource = useCallback(async () => {
    if (!onResourceRefresh) return;
    await onResourceRefresh();
  }, [onResourceRefresh]);

  // ---- Persist a single operation's new timing (no refresh) ----
  // Returns true on success, false when the task can't be resolved or the PUT fails.
  // Callers are responsible for refreshing once after a batch.
  const persistOperationTime = useCallback(
    async (taskId: string, newStart: number, newEnd: number): Promise<boolean> => {
      const scheduleId = parseScheduleIdFromTaskId(taskId);
      if (!scheduleId) return false;

      const node = findOperationNode(ganttNodes, scheduleId);
      if (!node) return false;
      const opData = node.data as StageOperation | undefined;
      if (!opData) return false;

      // Convert absolute hours → relative fields
      // newStart is absolute hours (e.g. Day 2 at 9:00 = 57)
      // API expects: operation_day (relative to stage), recommended_time (0-23.9)
      //
      // We need the stage's start_day to compute relative operation_day.
      // Since opData stores operation_day relative to stage, and
      // node.start_day was computed as stageStartDay + opData.operation_day + dayOffset,
      // we can derive stageStartDay from the current absolute position.
      const originalAbsoluteDay = node.start_day ?? 0;
      const originalOpDay = opData.operation_day ?? 0;
      const originalDayOffset = opData.recommended_day_offset ?? 0;
      const stageStartDay = originalAbsoluteDay - originalOpDay - originalDayOffset;

      await processTemplateV2Api.updateStageOperation(
        scheduleId,
        buildDraggedOperationTimingUpdate(newStart, newEnd, stageStartDay),
      );
      return true;
    },
    [ganttNodes],
  );

  // ---- Drag end handler (single task) ----
  const handleDragEnd = useCallback(
    async (taskId: string, newStart: number, newEnd: number): Promise<boolean> => {
      try {
        const ok = await persistOperationTime(taskId, newStart, newEnd);
        if (!ok) return false;
        await refreshData();
        return true;
      } catch (err: any) {
        wxbToast.error(err?.response?.data?.error || '更新操作时间失败');
        return false;
      }
    },
    [persistOperationTime, refreshData],
  );

  // ---- Resize end handler ----
  const handleResizeEnd = useCallback(
    async (taskId: string, newStart: number, newEnd: number): Promise<boolean> => {
      return handleDragEnd(taskId, newStart, newEnd);
    },
    [handleDragEnd],
  );

  // ---- Compute a task's current absolute start/end (hours) from the node tree ----
  // start_day is the absolute day; recommended_time is hour-of-day; standard_time is duration.
  const getTaskCurrentSpan = useCallback(
    (taskId: string): { start: number; end: number } | null => {
      const scheduleId = parseScheduleIdFromTaskId(taskId);
      if (!scheduleId) return null;
      const node = findOperationNode(ganttNodes, scheduleId);
      if (!node) return null;
      const opData = node.data as StageOperation | undefined;
      if (!opData) return null;

      const absoluteDay = node.start_day ?? 0;
      const startHour = absoluteDay * 24 + (opData.recommended_time ?? 0);
      const rawDuration =
        typeof opData.standard_time === 'string'
          ? parseFloat(opData.standard_time)
          : opData.standard_time;
      const duration = rawDuration && rawDuration > 0 ? rawDuration : 4;
      return { start: startHour, end: startHour + duration };
    },
    [ganttNodes],
  );

  // ---- Persist a batch of operation moves in one server transaction ----
  const persistBatch = useCallback(
    async (updates: Array<{ taskId: string; newStart: number; newEnd: number }>): Promise<boolean> => {
      const uniqueUpdates: Array<{ taskId: string; newStart: number; newEnd: number }> = [];
      const seenScheduleIds = new Set<number>();
      for (const update of updates) {
        const scheduleId = parseScheduleIdFromTaskId(update.taskId);
        if (scheduleId) {
          if (seenScheduleIds.has(scheduleId)) continue;
          seenScheduleIds.add(scheduleId);
        }
        uniqueUpdates.push(update);
      }

      if (uniqueUpdates.length === 0) return true;

      const batchPayload = uniqueUpdates.map((update) => {
        const scheduleId = parseScheduleIdFromTaskId(update.taskId);
        if (!scheduleId) return null;
        const node = findOperationNode(ganttNodes, scheduleId);
        const opData = node?.data as StageOperation | undefined;
        if (!node || !opData) return null;
        const stageStartDay = Number(node.start_day ?? 0)
          - Number(opData.operation_day ?? 0)
          - Number(opData.recommended_day_offset ?? 0);
        return {
          scheduleId,
          timing: buildDraggedOperationTimingUpdate(
            update.newStart,
            update.newEnd,
            stageStartDay,
          ),
        };
      });
      if (batchPayload.some((item) => item === null)) {
        return false;
      }
      try {
        await processTemplateV2Api.updateStageOperationTimesBatch(batchPayload as Array<{
          scheduleId: number;
          timing: ReturnType<typeof buildDraggedOperationTimingUpdate>;
        }>);
        await refreshData();
        return true;
      } catch (error: any) {
        wxbToast.error(error?.response?.data?.error || '批量时间调整失败，未保存任何操作');
        await refreshData();
        return false;
      }
    },
    [ganttNodes, refreshData],
  );

  // ---- Cascade group/equipment drag end ----
  // Shifts every affected operation by deltaHours from its CURRENT span, in one batch.
  // Reading current spans (not a captured snapshot) keeps the undo path — which re-invokes
  // this handler with -deltaHours against the refreshed positions — self-consistent.
  const handleGroupDragEnd = useCallback(
    async (_groupId: string, deltaHours: number, affectedTaskIds: string[]): Promise<boolean> => {
      const updates: Array<{ taskId: string; newStart: number; newEnd: number }> = [];
      for (const taskId of affectedTaskIds) {
        const span = getTaskCurrentSpan(taskId);
        if (!span) continue;
        updates.push({ taskId, newStart: span.start + deltaHours, newEnd: span.end + deltaHours });
      }
      return persistBatch(updates);
    },
    [getTaskCurrentSpan, persistBatch],
  );

  // ---- Multi-select drag end ----
  // Each update already carries its target absolute start/end from the canvas.
  const handleTasksDragEnd = useCallback(
    async (updates: Array<{ taskId: string; newStart: number; newEnd: number }>): Promise<boolean> => {
      return persistBatch(updates);
    },
    [persistBatch],
  );

  // ---- Delete task ----
  const handleDeleteTask = useCallback(
    async (taskId: string) => {
      const scheduleId = parseScheduleIdFromTaskId(taskId);
      if (!scheduleId) return;

      try {
        await processTemplateV2Api.deleteStageOperation(scheduleId);
        wxbToast.success('操作已删除');
        await refreshData();
        await refreshResource();
      } catch (err: any) {
        wxbToast.error(err?.response?.data?.error || '删除操作失败');
      }
    },
    [refreshData, refreshResource],
  );

  // ---- Auto-schedule ----
  const handleAutoSchedule = useCallback(async () => {
    try {
      setLoading(true);
      await api.post(`${API}/process-templates/${templateId}/auto-schedule`);
      wxbToast.success('自动排程完成');
      await refreshData();
      await refreshResource();
    } catch (err: any) {
      wxbToast.error(err?.response?.data?.error || '自动排程失败');
    } finally {
      setLoading(false);
    }
  }, [templateId, refreshData, refreshResource]);

  // ---- Refresh all ----
  const refreshAll = useCallback(async () => {
    await refreshResource();
  }, [refreshResource]);

  return {
    loading,
    handleDragEnd,
    handleResizeEnd,
    handleGroupDragEnd,
    handleTasksDragEnd,
    handleDeleteTask,
    handleAutoSchedule,
    refreshAll,
  };
}
